"use client"

import { HttpTypes } from "@medusajs/types"
import { ProductCard } from "@/components/organisms/ProductCard/ProductCard"
import { Button } from "@/components/atoms"
import { useCartContext } from "@/components/providers"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { toast } from "@/lib/helpers/toast"
import { useState } from "react"

interface CustomCatalogListProps {
  products: HttpTypes.StoreProduct[]
  locale: string
}

export const CustomCatalogList = ({ products, locale }: CustomCatalogListProps) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
      {products.map((product) => (
        <CustomProductCard key={product.id} product={product} locale={locale} />
      ))}
    </div>
  )
}

const CustomProductCard = ({ product, locale }: { product: HttpTypes.StoreProduct, locale: string }) => {
  const { addToCart, onAddToCart } = useCartContext()
  const [isAdding, setIsAdding] = useState(false)

  const { cheapestVariant, cheapestPrice } = getProductPrice({
    product,
  })

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!cheapestVariant?.id) return

    setIsAdding(true)

    const subtotal = +(cheapestPrice?.calculated_price_without_tax_number || 0)
    const total = +(cheapestPrice?.calculated_price_number || 0)

    const storeCartLineItem = {
      thumbnail: product.thumbnail || "",
      product_title: product.title,
      quantity: 1,
      subtotal,
      total,
      tax_total: total - subtotal,
      variant_id: cheapestVariant.id,
      product_id: product.id,
      variant: cheapestVariant,
    }

    // Optimistic update
    onAddToCart(storeCartLineItem, cheapestPrice?.currency_code || "usd")

    try {
      await addToCart({
        variantId: cheapestVariant.id,
        quantity: 1,
        countryCode: locale,
      })
      toast.success({
        title: "Added to cart",
        description: `${product.title} has been added to your cart.`,
      })
    } catch (error) {
      toast.error({
        title: "Error adding to cart",
        description: "Could not add product to cart. Please try again.",
      })
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="group flex flex-col h-full border rounded-2xl overflow-hidden bg-component shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1">
      <div className="flex-grow overflow-hidden">
        <ProductCard 
          product={product} 
          className="border-none w-full lg:w-full min-w-0 transition-transform duration-500 group-hover:scale-105" 
        />
      </div>
      <div className="p-5 bg-component-secondary/50 backdrop-blur-sm border-t">
        <Button
          onClick={handleAddToCart}
          loading={isAdding}
          disabled={!cheapestVariant || isAdding}
          className="w-full bg-action text-action-on-primary hover:bg-action-hover rounded-xl py-3 font-semibold transition-all duration-300 shadow-md hover:shadow-lg active:scale-95"
          size="large"
        >
          {cheapestVariant ? "Add to Cart" : "Out of Stock"}
        </Button>
      </div>
    </div>
  )
}
